import { I18n } from 'i18n-js'

// #region Format duration implementation

/**
 * Format time according to the format defined in the translations.
 *
 * @private
 *
 * @param {I18n} i18n The I18n instance.
 *
 * @param {Duration | number} duration The duration object containing the time units or a number of milliseconds.
 *
 * @param {(keyof Duration)[] | undefined} format The format specifying which units to include.
 *
 * @returns {string} The formatted duration.
 */
export function formatDuration(
  i18n: I18n,
  durationArg: Duration | number,
  scopeArg?: FormatDurationScope,
  formatArg?: (keyof Duration)[] | undefined,
): string {
  const duration: Duration =
    typeof durationArg === 'number' ? { milliseconds: durationArg } : durationArg
  const scope = scopeArg || 'default'
  const format = (formatArg && formatArg.length > 0 ? formatArg : defaultFormat).slice()

  // 1) Normalize to total milliseconds
  let totalMs = 0
  for (const key of Object.keys(duration) as (keyof Duration)[]) {
    const value = duration[key]
    if (!value) continue
    totalMs += value * unitMsMap[key]
  }

  const isNegative = totalMs < 0
  let remaining = Math.abs(totalMs)

  // 2) Determine included units in stable order
  const includeSet = new Set<keyof Duration>(format)
  const orderedIncluded = defaultFormat.filter(u => includeSet.has(u))

  // 3) Decompose across all units, but only emit and subtract for included ones.
  const label = makeFormatDurationScope(i18n, scope)
  const parts: string[] = []

  for (const unit of defaultFormat) {
    const unitMs = unitMsMap[unit]
    const q = Math.floor(remaining / unitMs)

    if (includeSet.has(unit)) {
      if (q > 0) {
        parts.push(label(q, unit))
        remaining -= q * unitMs
      } else {
        // Even when included, omit zero units per requirement/comment in tests
        // Do not subtract here to allow lower units to compute from full remaining
      }
    } else {
      // Excluded higher units are absorbed into the next included lower unit,
      // so we DO NOT subtract here.
    }
  }

  // 4) If nothing emitted, show 0 of the smallest included unit
  if (parts.length === 0) {
    const smallestIncluded =
      orderedIncluded[orderedIncluded.length - 1] ?? ('milliseconds' as const)
    parts.push(label(0, smallestIncluded))
  }

  // 5) Join using scope-specific separator
  const separator = scope === 'short' ? ' ' : ', '
  const result = parts.join(separator)

  return isNegative ? `-${result}` : result
}

// #endregion
// #region Definitions

export type FormatDurationScope = 'default' | 'long' | 'short'

export type Duration = {
  years?: number
  months?: number
  days?: number
  hours?: number
  minutes?: number
  seconds?: number
  milliseconds?: number
}

const defaultFormat: (keyof Duration)[] = [
  'years',
  'months',
  'days',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
]

const unitMsMap = {
  years: 365 * 24 * 60 * 60 * 1000,
  months: 30 * 24 * 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
  hours: 60 * 60 * 1000,
  minutes: 60 * 1000,
  seconds: 1000,
  milliseconds: 1,
} as const satisfies Record<keyof Duration, number>

// #endregion
// #region Format duration scopes

const makeFormatDurationScope =
  (i18n: I18n, scope: FormatDurationScope) => (value: number, durationKey: keyof Duration) => {
    if (scope === 'short') {
      return `${value}${durationKeyToShortScopeMap[durationKey]}`
    }

    return i18n.t(`datetime.distance_in_words.${durationKeyToLongScopeMap[durationKey]}`, {
      count: value,
    })
  }

const durationKeyToShortScopeMap = {
  years: 'y',
  months: 'm',
  days: 'd',
  hours: 'h',
  minutes: 'min',
  seconds: 's',
  milliseconds: 'ms',
} as const satisfies Record<keyof Duration, string>

const durationKeyToLongScopeMap = {
  years: 'x_years',
  months: 'x_months',
  days: 'x_days',
  hours: 'x_hours',
  minutes: 'x_minutes',
  seconds: 'x_seconds',
  milliseconds: 'x_milliseconds',
} as const satisfies Record<keyof Duration, string>

// #endregion
